import { getApplicationSubStatusDisplay } from '../../../utils/applicationSubStatus'
import { getScreeningRequestStatusDisplay } from '../../../utils/screeningStatus'
import { PipelineRecord, PipelineStage } from './types'

/** Demo top-level stage label (matches StageSwitcher tabs). */
export function resolveDemoStageLabel(record: PipelineRecord): string {
    if (record.tabLabel) {
        return record.tabLabel
    }
    switch (record.stage) {
        case 'leads':
            return 'Leads'
        case 'tours':
            return 'Leads'
        case 'app_sent':
            return 'Applications'
        case 'screening':
            return 'Screening'
        case 'leases':
        case 'lease_sent':
        case 'lease_signed':
            return 'Leases'
        case 'guarantor':
            return 'Screening'
        case 'future_resident':
            return 'Future Resident'
        default:
            return 'Leads'
    }
}

/** Where the person is within their current demo stage. */
export function resolveSubstageLabel(record: PipelineRecord): string {
    const stage = resolveDemoStageKey(record)

    if (stage === 'leads') {
        const account = record.account_status ?? 'none'
        if (account === 'invited' || record.invitation_sent_at) {
            return 'Invited'
        }
        return 'New'
    }

    if (stage === 'app_sent') {
        if (record.pending_application_review) {
            return 'Pending review'
        }
        if (record.application_status === 'submitted') {
            return 'Submitted'
        }
        return 'In progress'
    }

    if (stage === 'screening') {
        if (record.screening_result && record.screening_result !== 'pending') {
            const labels: Record<string, string> = {
                approved: 'Approved',
                conditional: 'Conditional',
                declined: 'Declined',
            }
            return labels[record.screening_result] ?? record.screening_result
        }
        const { label } = getScreeningRequestStatusDisplay(
            record.screening_status,
        )
        return label
    }

    if (stage === 'leases') {
        if (
            record.lease_package_status === 'signed' ||
            record.stage === 'lease_signed'
        ) {
            return 'Signed'
        }
        if (
            record.lease_package_status === 'awaiting_property' ||
            record.lease_awaiting_property
        ) {
            return 'Awaiting PM'
        }
        return 'Sent'
    }

    if (stage === 'future_resident') {
        if (record.checklist_total != null && record.checklist_done != null) {
            return `${record.checklist_done}/${record.checklist_total}`
        }
        return '—'
    }

    return getApplicationSubStatusDisplay(record.application_status).label
}

function resolveDemoStageKey(
    record: PipelineRecord,
): Extract<
    PipelineStage,
    'leads' | 'app_sent' | 'screening' | 'leases' | 'future_resident'
> {
    if (record.tabId === 'app_sent' || record.stage === 'app_sent') {
        return 'app_sent'
    }
    if (record.tabId === 'screening' || record.stage === 'screening') {
        return 'screening'
    }
    if (
        record.tabId === 'leases' ||
        record.stage === 'leases' ||
        record.stage === 'lease_sent' ||
        record.stage === 'lease_signed'
    ) {
        return 'leases'
    }
    if (
        record.tabId === 'future_resident' ||
        record.stage === 'future_resident'
    ) {
        return 'future_resident'
    }
    return 'leads'
}
